import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import SearchSection from './components/SearchSection';
import RegistryList from './components/RegistryList';
import MessageContainer from './components/MessageContainer';
import OnboardButton from './components/OnboardButton';
import AuthHeader from './components/AuthHeader';
import Footer from './components/Footer';
import { IconChevronUp } from './components/Icons';
import './discovery-smt-theme.css';

const AuthGate = ({ embedded, children }) => {
  const { loading, isAuthenticated } = useAuth();
  if (!loading && isAuthenticated) return children;
  const message = loading
    ? 'Checking your session...'
    : embedded
    ? 'Authentication required to access Discovery.'
    : 'Redirecting to sign in...';
  return (
    <div
      style={{
        position: embedded ? 'absolute' : 'fixed',
        inset: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#fff',
        color: '#0028CD',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        zIndex: 9999,
      }}
      role="status"
      aria-live="polite"
    >
      <div
        style={{
          width: 36,
          height: 36,
          borderRadius: '50%',
          border: '3px solid rgba(0, 40, 205, 0.18)',
          borderTopColor: '#0028CD',
          animation: 'adx-spin 0.9s linear infinite',
          marginBottom: 16,
        }}
      />
      <div style={{ fontSize: 13, fontWeight: 600, letterSpacing: 0.2 }}>
        {message}
      </div>
      <style>{`@keyframes adx-spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

const ScrollToTop = () => {
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const onScroll = () => setIsVisible(window.pageYOffset > 300);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  return (
    <button
      type="button"
      className={`ds-fab ${isVisible ? 'ds-fab--visible' : ''}`}
      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      aria-label="Scroll to top"
    >
      <IconChevronUp aria-hidden />
    </button>
  );
};

function App({ basePath = '/', embedded = false }) {
  const [viewMode, setViewMode] = useState('grid');

  const openDiscoverAAS = () => {
    if (embedded && typeof window.__ADX_HUB_OPEN_AAS_VIEWER__ === 'function') {
      window.__ADX_HUB_OPEN_AAS_VIEWER__();
      return;
    }
    const url = 'https://discovery-client.adx-hub.com/';
    if (window.self !== window.top) window.location.href = url;
    else window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <AuthProvider embedded={embedded}>
      <Router basename={basePath} future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <AuthGate embedded={embedded}>
        <div className={`ds-app${embedded ? ' ds-app--embedded' : ''}`}>
          {!embedded && (
            <header className="ds-topbar">
              <div className="ds-topbar__brand">
                <img src="/IDTA_Logo_Blue_Web_M.png" alt="IDTA" className="ds-topbar__logo" />
              </div>
              <AuthHeader />
            </header>
          )}

          <main className="ds-main">
            <div className="ds-inner">
              <header className="ds-content-header">
                <div className="ds-content-header__left">
                  <div className="ds-search">
                    <SearchSection />
                  </div>
                </div>
                <div className="ds-content-header__right">
                  <div className="ds-view-toggle" role="group" aria-label="View mode">
                    <button
                      type="button"
                      className={`ds-view-toggle__btn ${viewMode === 'grid' ? 'ds-view-toggle__btn--active' : ''}`}
                      onClick={() => setViewMode('grid')}
                      aria-pressed={viewMode === 'grid'}
                      aria-label="Grid view"
                    >
                      <svg className="ds-view-toggle__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                        <rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="3" y="14" width="7" height="7" /><rect x="14" y="14" width="7" height="7" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      className={`ds-view-toggle__btn ${viewMode === 'list' ? 'ds-view-toggle__btn--active' : ''}`}
                      onClick={() => setViewMode('list')}
                      aria-pressed={viewMode === 'list'}
                      aria-label="List view"
                    >
                      <svg className="ds-view-toggle__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                        <line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" /><line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" />
                      </svg>
                    </button>
                  </div>
                  <button
                    type="button"
                    className="ds-btn-primary ds-content-header__discover-btn"
                    onClick={openDiscoverAAS}
                    title="Open AAS Discovery Client"
                  >
                    Discover AAS
                  </button>
                  <span className="ds-content-header__divider" aria-hidden="true" />
                  <OnboardButton />
                </div>
              </header>

              <RegistryList viewMode={viewMode} />
            </div>
          </main>

          {!embedded && <Footer />}
          <MessageContainer />
          {!embedded && <ScrollToTop />}
        </div>
        </AuthGate>
      </Router>
    </AuthProvider>
  );
}

export default App;
